import React, { useCallback, useRef, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, RefreshControl, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { useTranslation } from 'react-i18next';
import { PhotoListType } from '~/constants/lists';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { VStack } from '@/components/gluestack/vstack';
import { Box } from '@/components/gluestack/box';
import { HStack } from '~/components/gluestack/hstack';
import { MyTypeFilterMenu } from '~/components/domain/common/MyTypeFilterMenu';
import { AlbumNotFound02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { PhotosService } from '~/services/domain/PhotosService';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { FlashList } from '@shopify/flash-list';
import { PhotoCard } from '@/components/domain/photos/PhotoCard';
import { PremiumUpgradeCard } from '@/components/domain/common/PremiumUpgradeCard';
import SafetySettingsContent from '@/components/domain/common/SafetySettingsContent';
import { SensitivePhotosReminderCard } from '@/components/domain/photos/SensitivePhotosReminderCard';
import { useIsFocused, useScrollToTop } from 'expo-router/react-navigation';
import { router } from 'expo-router';
import { getPhotoListItems } from '@/components/domain/photos/photoListItems';
import { ActionBar } from '@/components/layout/ActionBar';
import { HeaderMenuSelector, HeaderMenuSelectorTitle } from '@/components/ui/HeaderMenuSelector';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { Photo, PhotosView } from '@/models/Photo';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useInfiniteListUtils, clearStoredScrollPosition } from '@/hooks/useInfiniteListUtils';
import { useSensitivePhotosReminder } from '@/hooks/useSensitivePhotosReminder';
import { ContextSheet } from '@/components/ui/ContextSheet';

interface PhotosScreenProps {
  listType: PhotoListType;
}

const EMPTY_PHOTOS: Photo[] = [];

export function PhotosScreen({ listType }: PhotosScreenProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isLargeScreen = useIsLargeScreen();
  const isScreenActive = useIsFocused();
  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const photosForCarouselRef = useRef<Photo[]>(EMPTY_PHOTOS);
  const { width } = useWindowDimensions();
  const { onEndReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 5, desktop: 4 },
    maxColsOnScreen: { mobile: 3, desktop: 6 },
  });
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const [isSafetySheetOpen, setIsSafetySheetOpen] = useState(false);
  const {
    isDismissed: isSensitiveReminderDismissed,
    isLoaded: isSensitiveReminderLoaded,
    dismiss: dismissSensitiveReminder,
  } = useSensitivePhotosReminder();

  // Use the infinite list utils hook
  const { handleRefresh, isUserRefreshing, showHeaderRefetchIndicator, onScroll } =
    useInfiniteListUtils({
      queryKey: ['photos', listType],
      flashListRef,
      pageKey: `/photos/${listType}`, // Unique key for scroll position persistence and navigation refresh
    });

  // Registers scroll to top for when the tab is pressed
  useScrollToTop(flashListRef);

  const getServiceMethod = (type: PhotoListType) => {
    switch (type) {
      case 'latest':
        return PhotosService.getPhotosLatest;
      case 'network':
        return PhotosService.getPhotosLatestFollowings;
      case 'private':
        return PhotosService.getPhotosLatestPrivate;
      case 'favorites':
        return PhotosService.getPhotosLatestLiked;
      default:
        throw new Error(`Unknown list type: ${type}`);
    }
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['photos', listType],
    queryFn: async ({ pageParam }) => {
      const serviceMethod = getServiceMethod(listType);
      return await serviceMethod({
        ...buildPaginationParams(pageParam, pageSize),
        withDetails: true, // important for carousel
      });
    },

    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<PhotosView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.PhotosItems);
      const nonPremiumLimitReached = data.pages.some(
        (page) => page.NonPremiumLimitReached === true
      );
      return { ...data, items, nonPremiumLimitReached };
    }, []),
  });

  const navigateToList = (newListType: PhotoListType) => {
    if (newListType === listType) return;

    queryClient.cancelQueries({ queryKey: ['photos', listType] });

    // Sideways navigation between sibling lists should not restore the destination's
    // previous scroll position; only stack-pop returns (e.g. back from a photo) should.
    clearStoredScrollPosition(`/photos/${newListType}`);

    router.replace(`/photos/${newListType}` as any);
  };

  const ActionButtons = () => (
    <HeaderRefreshButton
      isLargeScreen={isLargeScreen}
      isRefreshing={isUserRefreshing}
      onPress={handleRefresh}
    />
  );

  // Determine photos per row based on screen size (only 2, 3, 4, or 6 columns)
  const getPhotosPerRow = () => {
    if (!isLargeScreen) {
      return 3; // 3 columns on small screens for photos
    }
    if (width >= 1536) {
      return 6;
    } else if (width >= 1024) {
      return 4;
    } else if (width >= 768) {
      return 3;
    }
    return 3;
  };
  const photosPerRow = getPhotosPerRow();

  // Memoize ItemWrapper to prevent re-renders
  const ItemWrapper = useMemo(
    () =>
      ({ children }: { children: React.ReactNode }) => (
        <Box className="mt-3 flex-1 pr-3 md:mb-2 md:mt-2 md:pr-4">{children}</Box>
      ),
    []
  );

  // Shared style for FlashList container
  const flashListContainerStyle = {
    paddingLeft: isLargeScreen ? 16 : 8,
    paddingTop: 8,
    paddingBottom: isLargeScreen ? safeAreaBottom : 0,
  };

  // Single-list loading pattern
  const isInitialLoading = isPending;
  const skeletonItems = useMemo(
    () => Array.from({ length: pageSize }, (_, i) => ({ key: `s-${i}` })),
    [pageSize]
  );

  // Get item count for the CountBadge
  const getItemCount = useCallback(() => {
    const queryData = queryClient.getQueryData(['photos', listType]) as any;
    return queryData?.pages?.[0]?.ItemCount || 0;
  }, [queryClient, listType]);

  const itemCount = getItemCount();
  const safeModeOff = data?.pages?.[0]?.SafeModeOff ?? null;
  const showSensitiveReminder =
    (Platform.OS === 'web' || Platform.OS === 'android') &&
    (listType === 'latest' || listType === 'network') &&
    safeModeOff === false &&
    isSensitiveReminderLoaded &&
    !isSensitiveReminderDismissed;

  const isMyTypeFiltered = useMemo(() => {
    return (data?.pages?.[0] as any)?.IsMyTypeFiltered ?? null;
  }, [data]);

  const photoListItems = useMemo(() => getPhotoListItems(t), [t]);
  const photoItems = (data?.items as Photo[] | undefined) ?? EMPTY_PHOTOS;
  photosForCarouselRef.current = photoItems;
  const getPhotosForCarousel = useCallback(() => photosForCarouselRef.current, []);

  return (
    <VStack className="flex-1">
      <ScreenHeader
        options={{
          hideBackButton: true,
          title: isLargeScreen ? t(`photos.${listType}`) : t('navigation.photos'),
          ...(isLargeScreen
            ? {
                headerTitle: () => (
                  <HeaderMenuSelectorTitle
                    value={listType}
                    items={photoListItems}
                    title={t(`photos.${listType}`)}
                  />
                ),
                headerLeft: () => (
                  <HStack className="items-center gap-3">
                    <MyTypeFilterMenu count={itemCount} isMyTypeFiltered={isMyTypeFiltered} />
                    {showHeaderRefetchIndicator ? <ActivityIndicator color="grey" /> : null}
                  </HStack>
                ),
                headerRight: () => <ActionButtons />,
              }
            : {
                headerBottom: () => (
                  <ActionBar
                    leftSection={
                      <HStack className="items-center gap-3 pr-3">
                        <HeaderMenuSelector
                          value={listType}
                          onChange={navigateToList}
                          items={photoListItems}
                          triggerLabel={t(`photos.${listType}_short`)}
                        />
                        <MyTypeFilterMenu count={itemCount} isMyTypeFiltered={isMyTypeFiltered} />
                      </HStack>
                    }
                    rightSection={<ActionButtons />}
                  />
                ),
              }),
        }}
      />

      {error ? (
        <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />
      ) : (
        <FlashList
          ref={flashListRef}
          key={photosPerRow}
          data={isInitialLoading ? skeletonItems : photoItems}
          renderItem={({ item, index }) => (
            <ItemWrapper>
              {isInitialLoading ? (
                <PhotoCard />
              ) : (
                <PhotoCard
                  photo={item}
                  showLikesComments={true}
                  getPhotosForCarousel={getPhotosForCarousel}
                  showProfileTooltip
                />
              )}
            </ItemWrapper>
          )}
          keyExtractor={(item, index) =>
            isInitialLoading ? `photo-skeleton-${index}` : `photo-${item.Id}`
          }
          numColumns={photosPerRow}
          onEndReached={() => {
            if (!isInitialLoading && hasNextPage && isScreenActive && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          maintainVisibleContentPosition={{
            autoscrollToTopThreshold: 10,
          }}
          onEndReachedThreshold={onEndReachedThreshold}
          ListEmptyComponent={
            <MessageView
              type="warning"
              text={t('photos.empty')}
              icon={AlbumNotFound02Icon}
              listEmpty
            />
          }
          ListHeaderComponent={
            showSensitiveReminder
              ? () => (
                  <Box className="mr-3 mt-3 md:mr-4">
                    <SensitivePhotosReminderCard
                      onOpenSettings={() => setIsSafetySheetOpen(true)}
                      onDismiss={dismissSensitiveReminder}
                    />
                  </Box>
                )
              : undefined
          }
          ListFooterComponent={() => (
            <Box style={{ width: '100%' }}>
              {isFetchingNextPage && (
                <Box className="m-4">
                  <ActivityIndicator color="grey" />
                </Box>
              )}
              {!isInitialLoading && data?.nonPremiumLimitReached && (
                <Box className="my-4 mr-3 md:mr-4">
                  <PremiumUpgradeCard listType="photos" />
                </Box>
              )}
            </Box>
          )}
          refreshControl={
            <RefreshControl refreshing={isUserRefreshing} onRefresh={handleRefresh} />
          }
          contentContainerStyle={flashListContainerStyle}
          onScroll={onScroll}
        />
      )}
      <ContextSheet
        isOpen={isSafetySheetOpen}
        onClose={() => {
          setIsSafetySheetOpen(false);
          refetch();
        }}
        contentComponent={SafetySettingsContent}
        title={t('settings.safety.title')}
      />
    </VStack>
  );
}
